import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Users, MessageSquare, Sparkles, TrendingUp, Award, Heart, Star, Zap, Gift } from 'lucide-react';
import { useTheme } from '../arrangement/ThemeContext';

const AuthGate = ({ 
  pageName = "this feature", 
  pageIcon = <Lock size={64} />,
  features = [],
  customMessage = null,
  showStats = true,
  backgroundColor = '#0a0a0a'
}) => {
  const navigate = useNavigate();
  const { colors } = useTheme();

  // Default features based on page type
  const getDefaultFeatures = () => {
    switch(pageName.toLowerCase()) {
      case 'discover':
        return [
          { icon: <Heart size={20} />, text: "Swipe and rate cannabis products", color: "#ef4444" },
          { icon: <Star size={20} />, text: "Build your personalized preferences", color: "#fbbf24" },
          { icon: <TrendingUp size={20} />, text: "Get product recommendations", color: "#00d4aa" },
          { icon: <Award size={20} />, text: "Earn XP and unlock achievements", color: "#8b5cf6" }
        ];
      case 'threads':
        return [
          { icon: <MessageSquare size={20} />, text: "Join community discussions", color: "#8b5cf6" },
          { icon: <Users size={20} />, text: "Connect with other enthusiasts", color: "#3b82f6" },
          { icon: <TrendingUp size={20} />, text: "Vote on trending topics", color: "#00d4aa" },
          { icon: <Sparkles size={20} />, text: "Share your experiences", color: "#f59e0b" }
        ];
      case 'grow room':
        return [
          { icon: <Sparkles size={20} />, text: "Track your growing journey", color: "#10b981" },
          { icon: <TrendingUp size={20} />, text: "Monitor plant progress", color: "#00d4aa" },
          { icon: <Users size={20} />, text: "Get expert growing tips", color: "#3b82f6" },
          { icon: <Award size={20} />, text: "Share your harvest results", color: "#8b5cf6" }
        ];
      case 'sesh rooms':
        return [
          { icon: <Users size={20} />, text: "Join private cannabis communities", color: "#3b82f6" },
          { icon: <MessageSquare size={20} />, text: "Chat with like-minded people", color: "#8b5cf6" },
          { icon: <Zap size={20} />, text: "Participate in group sessions", color: "#f59e0b" },
          { icon: <Gift size={20} />, text: "Share exclusive content", color: "#ef4444" }
        ];
      default:
        return [
          { icon: <Star size={20} />, text: "Personalized experience", color: "#fbbf24" },
          { icon: <Users size={20} />, text: "Connect with community", color: "#3b82f6" },
          { icon: <Award size={20} />, text: "Earn rewards and badges", color: "#8b5cf6" },
          { icon: <TrendingUp size={20} />, text: "Track your progress", color: "#00d4aa" }
        ];
    }
  };

  const displayFeatures = features.length > 0 ? features : getDefaultFeatures();

  const handleSignUp = () => {
    navigate('/login');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const stats = [
    { label: "Active Users", value: "50K+", icon: "👥" },
    { label: "Products Rated", value: "100K+", icon: "⭐" },
    { label: "Discussions", value: "25K+", icon: "💬" },
    { label: "Communities", value: "500+", icon: "🌿" }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: colors.bgPrimary,
      color: colors.textPrimary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '40px 24px',
        textAlign: 'center'
      }}>
        {/* Lock Icon with Animation */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '32px',
          animation: 'float 3s ease-in-out infinite'
        }}>
          <div style={{
            padding: '32px',
            background: 'linear-gradient(135deg, #8b5cf620 0%, #7c3aed20 100%)',
            borderRadius: '50%',
            border: '2px solid #8b5cf6',
            color: '#8b5cf6'
          }}>
            {pageIcon}
          </div>
        </div>

        {/* Main Heading */}
        <h1 style={{
          fontSize: '48px',
          fontWeight: '800',
          margin: '0 0 16px 0',
          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          lineHeight: '1.1'
        }}>
          Join the Community
        </h1>

        {/* Subtitle */}
        <p style={{
          fontSize: '24px',
          color: colors.textSecondary,
          margin: '0 0 40px 0',
          lineHeight: '1.4'
        }}>
          {customMessage || `Sign up to access ${pageName} and unlock the full Oak experience`}
        </p>

        {/* Features Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '24px',
          margin: '40px 0 48px 0'
        }}>
          {displayFeatures.map((feature, index) => (
            <div
              key={index}
              style={{
                background: colors.bgTertiary,
                border: `1px solid ${colors.border}`,
                borderRadius: '16px',
                padding: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                transition: 'all 0.3s ease',
                cursor: 'default'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = feature.color;
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = `0 8px 25px ${feature.color}20`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = colors.border;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{
                padding: '12px',
                borderRadius: '12px',
                background: `${feature.color}20`,
                color: feature.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {feature.icon}
              </div>
              <span style={{
                fontSize: '16px',
                fontWeight: '500',
                color: colors.textPrimary
              }}>
                {feature.text}
              </span>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '16px',
          justifyContent: 'center',
          flexWrap: 'wrap',
          marginBottom: '48px'
        }}>
          <button
            onClick={handleSignUp}
            style={{
              padding: '20px 40px',
              background: `linear-gradient(135deg, ${colors.accentPrimary} 0%, ${colors.accentSecondary} 100%)`,
              color: colors.bgPrimary,
              border: 'none',
              borderRadius: '16px',
              fontSize: '18px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: `0 4px 20px ${colors.accentPrimary}40`,
              minWidth: '180px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
              e.currentTarget.style.boxShadow = `0 8px 30px ${colors.accentPrimary}60`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = `0 4px 20px ${colors.accentPrimary}40`;
            }}
          >
            🚀 Sign Up Free
          </button>
          
          <button
            onClick={handleLogin}
            style={{
              padding: '20px 40px',
              background: colors.bgTertiary,
              color: colors.textPrimary,
              border: `2px solid ${colors.border}`,
              borderRadius: '16px',
              fontSize: '18px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              minWidth: '180px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#8b5cf6';
              e.currentTarget.style.background = 'linear-gradient(135deg, #8b5cf620 0%, #7c3aed20 100%)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = colors.border;
              e.currentTarget.style.background = colors.bgTertiary;
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Already a member? Log In
          </button>
        </div>

        {/* Community Stats */}
        {showStats && (
          <div style={{
            background: colors.bgSecondary,
            border: `1px solid ${colors.border}`,
            borderRadius: '20px',
            padding: '32px',
            margin: '48px 0'
          }}>
            <h3 style={{
              margin: '0 0 24px 0',
              fontSize: '20px',
              fontWeight: '600',
              color: colors.textPrimary
            }}>
              Join Our Growing Community
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '24px'
            }}>
              {stats.map((stat, index) => (
                <div key={index} style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: '32px',
                    marginBottom: '8px'
                  }}>
                    {stat.icon}
                  </div>
                  <div style={{
                    fontSize: '24px',
                    fontWeight: '700',
                    color: colors.accentPrimary,
                    marginBottom: '4px'
                  }}>
                    {stat.value}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: colors.textSecondary
                  }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Benefits List */}
        <div style={{
          textAlign: 'left',
          maxWidth: '500px',
          margin: '0 auto',
          padding: '32px',
          background: colors.bgTertiary,
          borderRadius: '20px',
          border: `1px solid ${colors.border}`
        }}>
          <h3 style={{
            margin: '0 0 24px 0',
            fontSize: '18px',
            fontWeight: '600',
            color: colors.textPrimary,
            textAlign: 'center'
          }}>
            Why Join Oak?
          </h3>
          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <li style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '14px',
              color: colors.textSecondary
            }}>
              <span style={{ color: colors.accentPrimary }}>✓</span>
              Free to join - no credit card required
            </li>
            <li style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '14px',
              color: colors.textSecondary
            }}>
              <span style={{ color: colors.accentPrimary }}>✓</span>
              Connect with 50,000+ cannabis enthusiasts
            </li>
            <li style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '14px',
              color: colors.textSecondary
            }}>
              <span style={{ color: colors.accentPrimary }}>✓</span>
              Personalized product recommendations
            </li>
            <li style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '14px',
              color: colors.textSecondary
            }}>
              <span style={{ color: colors.accentPrimary }}>✓</span>
              Track your cannabis journey with XP and levels
            </li>
          </ul>
        </div>

        {/* Privacy Note */}
        <p style={{
          fontSize: '12px',
          color: colors.textSecondary,
          marginTop: '32px',
          lineHeight: '1.4',
          opacity: 0.7
        }}>
          Your privacy is important to us. We never share your data and you can delete your account anytime.
        </p>
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
      `}</style>
    </div>
  );
};

export default AuthGate;